import classNames from 'classnames';
import React, { useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  GalleryVerticalEnd,
  CircleDollarSign,
  MonitorSmartphone,
  Settings,
  Lightbulb,
  X,
} from 'lucide-react';

import { useLayoutStore } from 'stores/useLayoutStore';
import { SizeContext } from 'components/context/SizeProvider';
import { Telegram } from 'components/Icon/Telegram';
import logo from 'assets/logo.svg';

import { CollapseButton } from './components/CollapseButton';
import { SidebarLink } from './components/SidebarLink';
import { ProfileDropdown } from './components/ProfileDropdown';
import { CreditsDisplay } from './components/CreditsDisplay';

export const Aside = () => {
  const { sidebarCollapse: isCollapsed, setSidebarCollapse } = useLayoutStore();
  const { isMobile } = useContext(SizeContext);
  const asideRef = useRef(null);

  useEffect(() => {
    if (!isMobile || isCollapsed) return undefined;
    const opener = document.activeElement;
    const content = document.getElementById('app-main');
    const overflow = document.body.style.overflow;
    const previousInert = content?.inert;
    if (content) content.inert = true;
    document.body.style.overflow = 'hidden';
    // Focus the sheet itself, not its first button: iOS Safari paints :focus-visible on a
    // script-focused button even after a tap, which put a stray accent ring on the close
    // control every time the menu opened. The container is tabIndex={-1}, so Tab from here
    // lands on Close (first focusable) and Escape still reaches the onKeyDown below.
    asideRef.current?.focus({ preventScroll: true });
    return () => {
      if (content) content.inert = previousInert;
      document.body.style.overflow = overflow;
      if (opener instanceof HTMLElement && opener.isConnected) opener.focus();
    };
  }, [isMobile, isCollapsed]);

  useEffect(() => {
    if (isMobile) setSidebarCollapse(true);
  }, [isMobile, setSidebarCollapse]);

  const { t } = useTranslation();

  return (
    <aside
      id="app-navigation"
      ref={asideRef}
      tabIndex={-1}
      aria-label={t('navBar.openNavigation')}
      onKeyDown={(e) => {
        if (!isMobile || isCollapsed || e.defaultPrevented) return;
        if (e.key === 'Escape') {
          e.preventDefault();
          setSidebarCollapse(true);
        }
        if (e.key === 'Tab') {
          const nodes = [...e.currentTarget.querySelectorAll('a[href], button:not([disabled])')].filter((node) => node.offsetParent !== null);
          const first = nodes[0];
          const last = nodes[nodes.length - 1];
          const onEdge = document.activeElement === first || document.activeElement === e.currentTarget;
          if (e.shiftKey && onEdge) { e.preventDefault(); last?.focus(); }
          else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first?.focus(); }
        }
      }}
      /*
       * Two very different things share this element:
       *  - from md up, the docked sidebar (w-50 wide, or the 64px icon rail when collapsed);
       *  - below md, a full-screen sheet over the page while open, and nothing at all
       *    while closed (the MobileHeader owns the trigger).
       * The sheet is translucent with a backdrop blur, so the page it covers shows through
       * faintly; `bg-ui-surface` is restored at md so the docked sidebar stays opaque.
       */
      className={classNames(
        'fixed top-0 flex-none text-ui-4 transition-all focus:outline-none',
        isCollapsed
          ? 'hidden md:block md:h-[100dvh] md:w-aside-collapsed md:bg-ui-surface md:py-4 z-20'
          : 'inset-0 z-30 bg-ui-glass backdrop-blur-md md:inset-auto md:top-0 md:z-20 md:h-[100dvh] md:w-50 md:bg-ui-surface md:pt-4 md:pb-4 md:backdrop-blur-none',
        { 'animate-fade-down': isMobile && !isCollapsed },
      )}
    >
      {/* Scrolls below md: as a full-screen sheet on a landscape phone (~390px tall) the
          credits and profile rows were clipped off the bottom with no way to reach them. */}
      <div className="flex h-full flex-col overflow-y-auto transition-all">
        {/* Mobile top row — same 3rem height and 1rem gutter as MobileHeader, with the logo
            in the same spot, so the sheet looks like the bar itself opened up. First in DOM
            on purpose: it makes Close the first Tab stop from the focused sheet. */}
        <div className="flex h-12 flex-none items-center justify-between border-b border-ui-hair px-4 md:hidden">
          <img src={logo} alt="" width={28} height={26} className="block" />
          <button
            type="button"
            onClick={() => setSidebarCollapse(true)}
            aria-label={t('navBar.closeNavigation')}
            aria-expanded
            aria-controls="app-navigation"
            className="-mr-2 flex h-10 w-10 items-center justify-center rounded-row text-ui-2 transition-colors duration-150 hover:bg-ui-overlay hover:text-ui focus:outline-none focus-visible:ring-2 focus-visible:ring-ui-accent-ring"
          >
            <X size={22} />
          </button>
        </div>

        <CollapseButton
          className={classNames('hidden md:inline-flex ml-auto mb-7', isCollapsed ? 'mr-auto' : 'mr-4')}
        />

        <img
          className={classNames('hidden md:block mb-8 border-l mx-auto', {
            'md:ml-8': !isCollapsed,
            'mx-auto': isCollapsed,
          })}
          src={logo}
          alt=""
          width={isCollapsed ? 42 : 82}
        />

        <nav className="menu text-ui-2 flex-1 px-3 pt-3 md:pt-0">
          <ul className="space-y-1">
            <li className="flex">
              <SidebarLink to="/app/playlists" label={t('navBar.playlists')} isCollapsed={isCollapsed}>
                <GalleryVerticalEnd />
                {!isCollapsed && <span>{t('navBar.playlists')}</span>}
              </SidebarLink>
            </li>
            <li className="flex">
              <SidebarLink to="/app/plans" label={t('navBar.plans')} isCollapsed={isCollapsed}>
                <CircleDollarSign />
                {!isCollapsed && <span>{t('navBar.plans')}</span>}
              </SidebarLink>
            </li>
            <li className="flex">
              <SidebarLink to="/app/devices" label={t('navBar.devices')} isCollapsed={isCollapsed}>
                <MonitorSmartphone />
                {!isCollapsed && <span>{t('navBar.devices')}</span>}
              </SidebarLink>
            </li>
            <li className="flex">
              <SidebarLink to="/app/ideas" label={t('navBar.ideas')} isCollapsed={isCollapsed}>
                <Lightbulb />
                {!isCollapsed && <span>{t('navBar.ideas')}</span>}
              </SidebarLink>
            </li>
            <li className="flex">
              <SidebarLink to="/app/settings" label={t('navBar.settings')} isCollapsed={isCollapsed}>
                <Settings />
                {!isCollapsed && <span>{t('navBar.settings')}</span>}
              </SidebarLink>
            </li>
          </ul>
        </nav>

        <div className="mt-auto">
          {/* Telegram */}
          <a
            href="https://t.me/UniPlayer"
            aria-label={t('navBar.telegramText')}
            target="_blank"
            rel="noopener noreferrer"
            className={classNames(
              'group flex items-center rounded-field text-ui-3 mx-3 p-3',
              'transition-all duration-200 ease-out',
              'hover:bg-ui-overlay hover:text-ui',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-ui-accent-ring',
              { 'justify-center': isCollapsed },
            )}
          >
            <Telegram className="shrink-0 transition-transform duration-200 ease-out group-hover:scale-110" />
            {!isCollapsed && (
              <span className="ml-2.5 text-sm">{t('navBar.telegramText')}</span>
            )}
          </a>

          {/* Credits */}
          <CreditsDisplay isCollapsed={isCollapsed} />

          {/* Profile */}
          <div
            className={classNames('border-t border-ui-border', {
              'mx-2': isCollapsed,
              'mx-3': !isCollapsed,
            })}
          >
            <ProfileDropdown isCollapsed={isCollapsed} />
          </div>
        </div>
      </div>
    </aside>
  );
};
